import React, { useEffect, useRef } from 'react';
import { FeaturedImagePreview } from '../components/featured/FeaturedImagePreview';
import { FeaturedVideoPreview } from '../components/featured/FeaturedVideoPreview';
import { HomeTopBar, HOME_TOPBAR_H } from '../components/home/layout/HomeTopBar';
import { resolveFeaturedById, type FeaturedPreviewItem } from '../config/featuredTemplates';
import { useFeaturedPreviewStore } from '../stores/featuredPreviewStore';
import { useWorksStore, selectResolvableWorks } from '../stores/worksStore';

interface FeaturedPreviewPageProps {
  /** 侧边栏是否折叠（决定内容区左缘让位 72 / 240，与 Sidebar 视觉宽度一致） */
  sidebarCollapsed?: boolean;
  /** 全部图像作品列表（预览页左栏 = 作品切换列表，与首页展示栏同源同序） */
  items?: FeaturedPreviewItem[];
  /** 全部视频作品列表（视频预览页底部切换条数据源，与 Bong 作品页同源同序） */
  videoItems?: FeaturedPreviewItem[];
  /** 点击左上角返回按钮：退出预览回到 Bong 精选列表（由 AppNew 传入，
      须同步切回 home 视图；单纯 close 会被下方 URL 恢复 effect 重新打开） */
  onBack?: () => void;
  /** 点击「查看制作流程」：打开对应作品的只读画布项目（由 AppNew 传入） */
  onOpenWorkflow?: (item: FeaturedPreviewItem) => void;
}

/**
 * Bong 精选作品预览页（图像 188:19 / 视频 202:501）——即梦式三段流式布局
 * - 顶部用户栏：首页同款 HomeTopBar（同一组件、原生流式几何，与首页视觉完全一致），
 *   左端 leading 放返回按钮
 * - 布局：内容区 = 顶栏下方、侧边栏右缘 → 视口右缘/下缘的剩余矩形，无整页缩放；
 *   预览组件内部实现三段流式——左侧作品列表列固定贴左、右侧信息栏 480 固定贴右、
 *   中段舞台在剩余空间内双锚定弹性伸缩（图像 contain 等比居中 / 视频列撑满剩余宽度），
 *   任意窗口尺寸下完整显示、无横向滚动、字号恒定；
 *   右侧信息栏背景上探至视口顶（顶部不预留顶栏位置，顶栏悬浮其上），栏内内容
 *   仍自顶栏下方起算（padding 补回 HOME_TOPBAR_H，位置与改造前一致）
 * - 数据来源：点击链路写入 featuredPreviewStore.current
 * - 刷新恢复：store 为空时从 URL query（/featured?type=image&id=wf-01）重建，
 *   用户作品查 worksStore，模板卡查 featuredTemplates
 */
export const FeaturedPreviewPage: React.FC<FeaturedPreviewPageProps> = ({ sidebarCollapsed = false, items, videoItems, onBack, onOpenWorkflow }) => {
  const current = useFeaturedPreviewStore((s) => s.current);
  const open = useFeaturedPreviewStore((s) => s.open);

  /* 刷新/直链恢复：current 为空时按 URL query 解析一次（restoredRef 保证只恢复一次：
     用户点返回 close() 后 URL 尚未同步，若不设守卫 effect 会立即从旧 query 重新 open，
     表现为返回按钮点击无响应） */
  const restoredRef = useRef(false);
  useEffect(() => {
    if (current || restoredRef.current) return;
    restoredRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type');
    const id = params.get('id');
    if ((type === 'image' || type === 'video') && id) {
      // 合并解析：publishedWorks + 精选会话态（匿名访客刷新恢复时精选作品不在 publishedWorks）
      const item = resolveFeaturedById(type, id, selectResolvableWorks(useWorksStore.getState()));
      if (item) open(item);
      else restoredRef.current = false;
    } else {
      restoredRef.current = false;
    }
  }, [current, open]);

  if (!current) return null;

  return (
    <div className="fixed inset-0 z-[160] overflow-hidden bg-[#121212]">
      {/* 顶部用户栏：直接复用首页 HomeTopBar（同一组件、原生流式几何，与首页视觉
          完全一致，任何窗口尺寸下不再随 fit 缩放被压小）。左端 leading 放返回按钮
          （原画布内返回按钮上提，与设计稿「顶部栏内白色回退箭头」一致）。
          fixed 定位相对视口：本页根容器无 transform，不会被劫持 */}
      <HomeTopBar
        collapsed={sidebarCollapsed}
        /* 全屏预览页自身无滚动（fixed inset-0 overflow-hidden），且文档 scrollY
           残留首页位置——关闭滚动感知，顶栏常透明（浮动胶囊式，同画布页顶栏） */
        scrollAware={false}
        leading={
          onBack ? (
            /* 设计稿 202:501 顶部栏 206:1034：36x36 圆角 12 白 65% 圆底（无描边）+
               白 2px 圆头线条箭头（折线 M18 12 L12 18 L18 24 + 杆 M12 18 H25）；
               栏内 (24, 26)——x 由 HomeTopBar 栏 px-4 + pl-2 补足 24，y 由栏
               paddingTop 16 后的居中线（中心 44）天然落位 26..62，无需额外补偿 */
            <button
              type="button"
              aria-label="返回"
              onClick={onBack}
              className="block h-9 w-9 cursor-pointer transition-opacity hover:opacity-80"
            >
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="36" height="36" rx="12" fill="white" fillOpacity="0.65" />
                <path
                  d="M18 12L12 18L18 24"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M12 18H25" stroke="white" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          ) : undefined
        }
      />
      {/* 内容区：顶栏下方、侧边栏右缘 → 视口右缘/下缘的剩余矩形，原生流式无缩放。
          三段式布局由预览组件内部实现：左列表贴左、右栏 480 贴右、
          中段舞台在剩余空间内双锚定弹性伸缩，任何窗口尺寸完整显示。
          右栏背景上探至视口顶（顶部不预留顶栏位置）——本层不设 overflow-hidden
          以免裁掉上探段；右栏滑出右缘的裁切由页面根容器 overflow-hidden 承担 */}
      <div
        className="absolute"
        style={{ top: HOME_TOPBAR_H, left: sidebarCollapsed ? 72 : 240, right: 0, bottom: 0 }}
      >
        {current.type === 'image' ? (
          <FeaturedImagePreview item={current} items={items} onOpenWorkflow={onOpenWorkflow} />
        ) : (
          <FeaturedVideoPreview item={current} items={videoItems} onOpenWorkflow={onOpenWorkflow} />
        )}
      </div>
    </div>
  );
};
